import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { login, loginErrorText, register } from "@/lib/api";
import { saveSession } from "@/lib/storage";
import { useApiKey } from "@/hooks/useApiKey";
import { cn } from "@/lib/utils";

interface AuthModalProps {
  open: boolean;
  initialTab?: AuthTab;
  onClose: () => void;
}

type AuthTab = "login" | "register";
type LoginMethod = "password" | "api_key";

interface FormMessage {
  text: string;
  kind: "" | "ok" | "error";
}

const EMPTY_MSG: FormMessage = { text: "", kind: "" };

export function AuthModal({ open, initialTab = "login", onClose }: AuthModalProps) {
  const [tab, setTab] = useState<AuthTab>(initialTab);
  const [loginMethod, setLoginMethod] = useState<LoginMethod>("password");
  const [loginMsg, setLoginMsg] = useState<FormMessage>(EMPTY_MSG);
  const [registerMsg, setRegisterMsg] = useState<FormMessage>(EMPTY_MSG);
  const [submitting, setSubmitting] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const apiKeyRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const { save } = useApiKey();
  const navigate = useNavigate();

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab]);

  useEffect(() => {
    if (!open) return;
    document.body.classList.add("auth-modal-open");
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    const t = window.setTimeout(() => {
      if (tab === "register") {
        emailRef.current?.focus();
      } else if (loginMethod === "password") {
        usernameRef.current?.focus();
      } else {
        apiKeyRef.current?.focus();
      }
    }, 40);
    return () => {
      document.body.classList.remove("auth-modal-open");
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
    };
  }, [open, tab, loginMethod, onClose]);

  const handleLogin = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const data = new FormData(ev.currentTarget);
    const remember = data.get("remember") === "on";
    setSubmitting(true);
    try {
      const resp = loginMethod === "password"
        ? await login({
            method: "password",
            username: String(data.get("username") || "").trim().toLowerCase(),
            password: String(data.get("password") || ""),
          })
        : await login({
            method: "api_key",
            api_key: String(data.get("api_key") || "").trim(),
          });
      save(resp.session_token, remember);
      saveSession(resp.session_token, remember);
      setLoginMsg({ text: "登录成功", kind: "ok" });
      navigate("/console");
    } catch (err) {
      setLoginMsg({ text: loginErrorText(err), kind: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleRegister = async (ev: FormEvent<HTMLFormElement>) => {
    ev.preventDefault();
    const data = new FormData(ev.currentTarget);
    const email = String(data.get("email") || "").trim();
    const password = String(data.get("password") || "");
    const username = String(data.get("username") || "").trim().toLowerCase();
    if (!email || !password) {
      setRegisterMsg({ text: "邮箱和密码必填", kind: "error" });
      return;
    }
    if (password.length < 8) {
      setRegisterMsg({ text: "密码至少 8 位", kind: "error" });
      return;
    }
    setRegisterMsg({ text: "正在创建账号…", kind: "" });
    setSubmitting(true);
    try {
      const resp = await register({ email, password, username: username || undefined });
      save(resp.session_token, true);
      saveSession(resp.session_token, true);
      setRegisterMsg({ text: "注册成功", kind: "ok" });
      navigate("/console");
    } catch (err) {
      setRegisterMsg({
        text: err instanceof Error ? err.message : "提交失败",
        kind: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      className={cn("auth-modal", !open && "hidden")}
      id="console"
      aria-hidden={!open}
    >
      <div className="auth-modal-backdrop" onClick={onClose} />
      <div className="auth-modal-dialog" role="dialog" aria-modal="true" aria-labelledby="auth-modal-title">
        <button
          className="auth-modal-close"
          type="button"
          aria-label="关闭登录窗口"
          onClick={onClose}
        >
          ×
        </button>
        <div className="console-shell auth-modal-shell">
          <div className="console-auth">
            <div className="console-auth-copy">
              <span className="section-label">Agentfly Console</span>
              <h2 className="section-title" id="auth-modal-title">登录平台</h2>
              <p className="section-desc">
                用账号密码登录控制台查看用量、API Key 和一键安装命令。也支持用旧的 API Key 直接登录。
              </p>
            </div>

            <div className="auth-box">
              <div className="auth-tabs" role="tablist" aria-label="登录与注册">
                <button
                  className={cn("auth-tab", tab === "login" && "active")}
                  type="button"
                  role="tab"
                  aria-selected={tab === "login"}
                  onClick={() => setTab("login")}
                >
                  登录
                </button>
                <button
                  className={cn("auth-tab", tab === "register" && "active")}
                  type="button"
                  role="tab"
                  aria-selected={tab === "register"}
                  onClick={() => setTab("register")}
                >
                  注册
                </button>
              </div>

              <form
                className={cn("auth-form", tab !== "login" && "hidden")}
                onSubmit={handleLogin}
              >
                <div className="auth-method-tabs" role="tablist" aria-label="登录方式">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={loginMethod === "password"}
                    className={cn("auth-method-tab", loginMethod === "password" && "active")}
                    onClick={() => setLoginMethod("password")}
                  >
                    账号密码
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={loginMethod === "api_key"}
                    className={cn("auth-method-tab", loginMethod === "api_key" && "active")}
                    onClick={() => setLoginMethod("api_key")}
                  >
                    API Key
                  </button>
                </div>

                {loginMethod === "password" ? (
                  <>
                    <label className="field">
                      <span>用户名</span>
                      <input
                        name="username"
                        type="text"
                        autoComplete="username"
                        placeholder="4-32 位 a-z, 0-9, _"
                        required
                        ref={usernameRef}
                      />
                    </label>
                    <label className="field">
                      <span>密码</span>
                      <input
                        name="password"
                        type="password"
                        autoComplete="current-password"
                        placeholder="至少 8 位"
                        required
                        minLength={8}
                      />
                    </label>
                  </>
                ) : (
                  <label className="field">
                    <span>API Key</span>
                    <input
                      name="api_key"
                      type="password"
                      autoComplete="off"
                      placeholder="粘贴完整 API Key"
                      required
                      ref={apiKeyRef}
                    />
                    <small className="field-note">
                      这里填写 API Key，不是 apis.agentfly.net 或 https://apis.agentfly.net/v1。
                    </small>
                  </label>
                )}

                <label className="check-field">
                  <input name="remember" type="checkbox" />
                  <span>在此浏览器保留登录状态</span>
                </label>
                <button className="btn-primary auth-submit" type="submit" disabled={submitting}>
                  登录平台
                </button>
                <p className={cn("form-message", loginMsg.kind)} aria-live="polite" data-login-message>
                  {loginMsg.text}
                </p>
              </form>

              <form
                className={cn("auth-form", tab !== "register" && "hidden")}
                onSubmit={handleRegister}
              >
                <label className="field">
                  <span>邮箱</span>
                  <input
                    name="email"
                    type="email"
                    autoComplete="email"
                    placeholder="you@example.com"
                    required
                    ref={emailRef}
                  />
                </label>
                <label className="field">
                  <span>用户名 <small>（选填，留空自动生成）</small></span>
                  <input
                    name="username"
                    type="text"
                    autoComplete="username"
                    placeholder="4-32 位 a-z, 0-9, _"
                  />
                </label>
                <label className="field">
                  <span>密码</span>
                  <input
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="至少 8 位"
                    required
                    minLength={8}
                  />
                </label>
                <button className="btn-primary auth-submit" type="submit" disabled={submitting}>
                  注册并进入控制台
                </button>
                <p className={cn("form-message", registerMsg.kind)} aria-live="polite" data-register-message>
                  {registerMsg.text}
                </p>
                <p className="auth-hint">注册后会立刻为你签发一把专属 API Key。</p>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
